/**
 * device/overlay.ts — The Screen Within The Screen
 *
 * Draws the device overlay directly onto the main canvas. This IS
 * the phone/tablet screen — rendered after the HUD, on top of everything.
 *
 * The overlay handles the device frame (shell, bezel, screen), status bar,
 * home screen grid, app chrome (top bar, back button), and transitions.
 * Individual app content is delegated to the active DeviceApp.
 *
 * The device can be dragged to any position on screen — grab the frame
 * (bezel edge) and move it. Release near center to snap back smoothly.
 * This lets the player peek behind the device at the room below.
 *
 * Pure Canvas 2D. No DOM. No HTML. Just paths and pixels.
 */

import type { GameState } from "../engine/state.js";
import type { DeviceState } from "./state.js";
import { isDeviceReady, getActiveDeviceConfig, getAppBadge } from "./state.js";
import { getApps, getApp, drawRoundRect, drawText } from "./app.js";
import type { Rect, DeviceApp } from "./app.js";

// ─── Layout Constants ─────────────────────────────────────────────────────

/** Phone dimensions (portrait) */
const PHONE_W = 180;
const PHONE_H = 320;

/** Pad dimensions (landscape) */
const PAD_W = 380;
const PAD_H = 260;

/** Frame inset (bezel thickness) */
const FRAME_INSET = 4;

/** Corner radius for the device frame */
const FRAME_RADIUS = 14;

/** Status bar height */
const STATUS_BAR_H = 18;

/** Top navigation bar height (app view) */
const NAV_BAR_H = 32;

/** Home indicator height at bottom */
const HOME_INDICATOR_H = 14;

/** App icon size on home screen */
const ICON_SIZE = 40;

/** App icon grid spacing */
const ICON_GAP = 12;

/** Icon label font size */
const ICON_LABEL_SIZE = 7;

/** Duration of open/close animation in seconds */
const ANIM_DURATION = 0.25;

/** Width of the draggable bezel zone (pixels from frame edge inward) */
const DRAG_BEZEL = 14;

/** Distance from center below which the device snaps back */
const SNAP_THRESHOLD = 40;

/** Spring constant for snap-back animation */
const SNAP_SPEED = 12;

// ─── Colors ───────────────────────────────────────────────────────────────

const C_SCREEN_BG   = "#0a0a12";
const C_STATUS_TEXT  = "rgba(255,255,255,0.7)";
const C_NAV_BG      = "rgba(20,14,8,0.88)";
const C_NAV_TEXT     = "#f5e6d0";
const C_BACK_ARROW   = "#e8a050";
const C_HOME_BG      = "rgba(15,15,25,0.95)";
const C_ICON_LABEL   = "rgba(255,255,255,0.8)";
const C_BADGE_BG     = "#ff3b30";
const C_BADGE_TEXT    = "#ffffff";
const C_INDICATOR     = "rgba(255,180,80,0.30)";

// ─── Transition State ─────────────────────────────────────────────────────

interface TransitionState {
  type: "opening" | "closing" | "app_enter" | "app_exit" | "none";
  progress: number;  // 0-1
  fromApp: string | null;
  toApp: string | null;
}

// ─── DeviceOverlay ────────────────────────────────────────────────────────

export class DeviceOverlay {
  private transition: TransitionState = { type: "none", progress: 0, fromApp: null, toApp: null };
  private lastDt = 0;
  private iconPressIndex = -1;
  private iconPressTimer = 0;

  // ── Animation origin (character hand position when device opens) ──
  private animOriginX = 0;
  private animOriginY = 0;

  // ── Drag state (device frame repositioning) ─────────────────────────
  private dragging = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragOffsetStartX = 0;
  private dragOffsetStartY = 0;
  /** Pointer position fed from game loop each frame */
  private pointerX = 0;
  private pointerY = 0;
  private pointerDown = false;
  private pointerWasDown = false;

  // ── App content drag state (scroll inside apps) ────────────────────
  private appDragging = false;
  private appDragLastY = 0;
  private appDragVelocity = 0;
  private appDragStartY = 0;
  private appDragStartX = 0;
  /** Accumulated distance — distinguish tap from drag */
  private appDragDist = 0;
  /** Pending tap to fire if drag distance stays below threshold */
  private _pendingTapApp: string | null = null;
  private _pendingTapX = 0;
  private _pendingTapY = 0;

  /**
   * Feed current pointer state from the game loop. Call every frame.
   */
  updatePointer(x: number, y: number, isDown: boolean): void {
    this.pointerX = x;
    this.pointerY = y;
    this.pointerWasDown = this.pointerDown;
    this.pointerDown = isDown;
  }

  /**
   * Update animation timers.
   * Call once per frame before draw().
   */
  update(state: GameState, dt: number): void {
    this.lastDt = dt;
    const device = state.player.device;

    // Open/close animation
    if (this.transition.type === "opening") {
      this.transition.progress += dt / ANIM_DURATION;
      if (this.transition.progress >= 1) {
        this.transition.progress = 1;
        this.transition.type = "none";
        device.animPhase = "held";
        device.animProgress = 1;
      }
    } else if (this.transition.type === "closing") {
      this.transition.progress += dt / ANIM_DURATION;
      if (this.transition.progress >= 1) {
        this.transition = { type: "none", progress: 0, fromApp: null, toApp: null };
        device.activeDevice = null;
        device.activeApp = null;
        device.animPhase = "stowed";
        device.animProgress = 0;
        state.ui.deviceOpen = false;
        state.ui.deviceScreenRect = null;
      }
    } else if (this.transition.type === "app_enter" || this.transition.type === "app_exit") {
      this.transition.progress += dt / 0.18; // 180ms app transition
      if (this.transition.progress >= 1) {
        this.transition.progress = 1;
        if (this.transition.type === "app_enter") {
          device.activeApp = this.transition.toApp;
        } else {
          device.activeApp = null;
        }
        this.transition.type = "none";
      }
    }

    // Icon press feedback decay
    if (this.iconPressTimer > 0) {
      this.iconPressTimer -= dt;
      if (this.iconPressTimer <= 0) {
        this.iconPressIndex = -1;
      }
    }

    // ── Device frame drag handling ─────────────────────────────────────
    if (this.dragging) {
      if (this.pointerDown) {
        device.offsetX = this.dragOffsetStartX + (this.pointerX - this.dragStartX);
        device.offsetY = this.dragOffsetStartY + (this.pointerY - this.dragStartY);
      } else {
        this.dragging = false;
      }
    }

    // ── App content drag (scroll) ────────────────────────────────────
    if (this.appDragging) {
      if (this.pointerDown) {
        const dy = this.pointerY - this.appDragLastY;
        const dx = this.pointerX - this.appDragStartX;
        const totalDY = this.pointerY - this.appDragStartY;
        // Track max displacement from start (not accumulated per-frame jitter)
        this.appDragDist = Math.max(Math.abs(dx), Math.abs(totalDY));
        // Smooth velocity tracking (exponential moving average)
        this.appDragVelocity = this.appDragVelocity * 0.6 + (dy / Math.max(dt, 0.001)) * 0.4;
        this.appDragLastY = this.pointerY;
        // Delegate to active app
        if (device.activeApp && Math.abs(dy) > 0.1) {
          const app = getApp(device.activeApp);
          app?.handleDrag?.(-dy, state);
        }
      } else {
        // Released — was it a tap or a drag?
        const TAP_THRESHOLD = 20;
        if (this.appDragDist < TAP_THRESHOLD && this._pendingTapApp) {
          // Short movement — dispatch as tap
          const app = getApp(this._pendingTapApp);
          app?.handleTap(this._pendingTapX, this._pendingTapY, state);
          state.ui.uiSounds.push("device_tap");
        } else if (device.activeApp) {
          // Real drag ended — fire momentum
          const app = getApp(device.activeApp);
          app?.handleDragEnd?.(-this.appDragVelocity, state);
        }
        this.appDragging = false;
        this.appDragVelocity = 0;
        this._pendingTapApp = null;
      }
    }

    // ── Momentum tick for apps (even after drag ends) ────────────────
    // Handled per-app in their drawContent via elapsed/dt

    // ── Snap-back animation ───────────────────────────────────────────
    // When not dragging, smoothly spring back toward center if close
    if (!this.dragging && (device.offsetX !== 0 || device.offsetY !== 0)) {
      const dist = Math.sqrt(device.offsetX * device.offsetX + device.offsetY * device.offsetY);
      if (dist < SNAP_THRESHOLD) {
        // Spring toward center
        const decay = Math.exp(-SNAP_SPEED * dt);
        device.offsetX *= decay;
        device.offsetY *= decay;
        // Snap exactly to 0 when close enough
        if (Math.abs(device.offsetX) < 0.5 && Math.abs(device.offsetY) < 0.5) {
          device.offsetX = 0;
          device.offsetY = 0;
        }
      }
    }
  }

  /**
   * Draw the device overlay onto the main canvas.
   * Returns the screen rect for hit-testing (null if not visible).
   */
  draw(ctx: CanvasRenderingContext2D, state: GameState, sw: number, sh: number): Rect | null {
    const device = state.player.device;
    if (!device.activeDevice && this.transition.type !== "closing") return null;

    const isPad = device.activeDevice === "pad";
    const devW = isPad ? PAD_W : PHONE_W;
    const devH = isPad ? PAD_H : PHONE_H;

    // Compute device position (centered + user offset)
    const cx = Math.round(sw / 2 + device.offsetX);
    const cy = Math.round(sh / 2 + device.offsetY);

    // Clamp to screen with padding so device doesn't vanish
    const pad = 40;
    const clampedCx = Math.max(devW / 2 - pad, Math.min(sw - devW / 2 + pad, cx));
    const clampedCy = Math.max(devH / 2 - pad, Math.min(sh - devH / 2 + pad, cy));

    const frameX = clampedCx - devW / 2;
    const frameY = clampedCy - devH / 2;

    // Apply open/close scale animation — origin lerps from character hand
    let scale = 1;
    let alpha = 1;
    let pivotX = clampedCx;
    let pivotY = clampedCy;
    if (this.transition.type === "opening") {
      const raw = this.transition.progress;
      const t = easeOutBack(raw);
      scale = 0.1 + t * 0.9;
      alpha = Math.min(1, raw * 2); // fade in faster than scale
      // Pivot lerps from character hand to device center
      pivotX = this.animOriginX + (clampedCx - this.animOriginX) * easeOutCubic(raw);
      pivotY = this.animOriginY + (clampedCy - this.animOriginY) * easeOutCubic(raw);
    } else if (this.transition.type === "closing") {
      const raw = 1 - this.transition.progress;
      const t = raw;
      scale = 0.1 + t * 0.9;
      alpha = Math.min(1, raw * 2);
      pivotX = this.animOriginX + (clampedCx - this.animOriginX) * easeOutCubic(raw);
      pivotY = this.animOriginY + (clampedCy - this.animOriginY) * easeOutCubic(raw);
    }

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(pivotX, pivotY);
    ctx.scale(scale, scale);
    ctx.translate(-pivotX, -pivotY);

    // ── Scrim behind device (reduced when offset from center) ──
    const scrimAlpha = 0.35 * alpha * Math.max(0.15, 1 - Math.sqrt(device.offsetX ** 2 + device.offsetY ** 2) / 400);
    ctx.fillStyle = `rgba(0,0,0,${scrimAlpha.toFixed(3)})`;
    ctx.fillRect(-sw, -sh, sw * 3, sh * 3);

    const config = getActiveDeviceConfig(device);
    const shellColor = config?.color ?? "#3a3a3a";
    const shellDark = darken(shellColor, 0.3);

    // ── Drop shadow (subtle depth cue, stronger when lifted/dragged) ──
    const shadowAlpha = this.dragging ? 0.45 : 0.2;
    const shadowBlur = this.dragging ? 24 : 12;
    ctx.save();
    ctx.shadowColor = `rgba(0,0,0,${shadowAlpha})`;
    ctx.shadowBlur = shadowBlur;
    ctx.shadowOffsetX = this.dragging ? 4 : 2;
    ctx.shadowOffsetY = this.dragging ? 6 : 3;
    drawRoundRect(ctx, frameX, frameY, devW, devH, FRAME_RADIUS, shellColor);
    ctx.restore();

    // ── Device Frame ──
    drawRoundRect(ctx, frameX, frameY, devW, devH, FRAME_RADIUS, shellColor);
    // Bezel edge
    ctx.strokeStyle = shellDark;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    // Inner highlight (top edge catches light)
    ctx.strokeStyle = `rgba(255,255,255,0.12)`;
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.arc(clampedCx, frameY + FRAME_RADIUS, devW / 2 - FRAME_RADIUS, Math.PI + 0.3, -0.3);
    ctx.stroke();

    // ── Drag indicator — subtle grab handles on bezel when hovering ──
    if (isDeviceReady(device) && !this.dragging) {
      const isOverBezel = this.isOverBezel(this.pointerX, this.pointerY, frameX, frameY, devW, devH);
      if (isOverBezel) {
        // Draw subtle grip lines on nearest bezel edge
        ctx.save();
        ctx.globalAlpha = 0.15;
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 0.8;
        for (let i = -2; i <= 2; i++) {
          const gy = clampedCy + i * 6;
          ctx.beginPath();
          ctx.moveTo(frameX + 3, gy);
          ctx.lineTo(frameX + DRAG_BEZEL - 2, gy);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(frameX + devW - DRAG_BEZEL + 2, gy);
          ctx.lineTo(frameX + devW - 3, gy);
          ctx.stroke();
        }
        ctx.restore();
      }
    }

    // ── Screen Area ──
    const screenX = frameX + FRAME_INSET;
    const screenY = frameY + FRAME_INSET;
    const screenW = devW - FRAME_INSET * 2;
    const screenH = devH - FRAME_INSET * 2;
    const screenR = FRAME_RADIUS - 2;

    drawRoundRect(ctx, screenX, screenY, screenW, screenH, screenR, C_SCREEN_BG);

    // Clip to screen area
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(screenX + screenR, screenY);
    ctx.lineTo(screenX + screenW - screenR, screenY);
    ctx.arcTo(screenX + screenW, screenY, screenX + screenW, screenY + screenR, screenR);
    ctx.lineTo(screenX + screenW, screenY + screenH - screenR);
    ctx.arcTo(screenX + screenW, screenY + screenH, screenX + screenW - screenR, screenY + screenH, screenR);
    ctx.lineTo(screenX + screenR, screenY + screenH);
    ctx.arcTo(screenX, screenY + screenH, screenX, screenY + screenH - screenR, screenR);
    ctx.lineTo(screenX, screenY + screenR);
    ctx.arcTo(screenX, screenY, screenX + screenR, screenY, screenR);
    ctx.closePath();
    ctx.clip();

    // ── Wallpaper — rich seasonal art ──
    const season = state.time.season;
    const wpGrad = ctx.createLinearGradient(screenX, screenY, screenX, screenY + screenH);

    if (season === "spring") {
      // Soft cherry blossom dawn — pink to warm cream
      wpGrad.addColorStop(0, "#2a1828"); wpGrad.addColorStop(0.4, "#3a2030");
      wpGrad.addColorStop(0.7, "#2a2838"); wpGrad.addColorStop(1, "#1a1820");
    } else if (season === "summer") {
      // Deep twilight sky — indigo to deep blue with warm horizon
      wpGrad.addColorStop(0, "#0a0a2e"); wpGrad.addColorStop(0.3, "#141840");
      wpGrad.addColorStop(0.7, "#1a2848"); wpGrad.addColorStop(1, "#2a2030");
    } else if (season === "autumn") {
      // Warm ember glow — deep burgundy to golden amber
      wpGrad.addColorStop(0, "#2a1410"); wpGrad.addColorStop(0.3, "#3a2018");
      wpGrad.addColorStop(0.7, "#2a1a12"); wpGrad.addColorStop(1, "#1a1008");
    } else {
      // Frost crystal — deep navy to icy silver-blue
      wpGrad.addColorStop(0, "#0a1020"); wpGrad.addColorStop(0.3, "#141830");
      wpGrad.addColorStop(0.7, "#1a2040"); wpGrad.addColorStop(1, "#0e1428");
    }
    ctx.fillStyle = wpGrad;
    ctx.fillRect(screenX, screenY, screenW, screenH);

    // Seasonal decorative elements drawn on wallpaper
    const wpTime = (state.time.gameHour + Date.now() * 0.00001) * 0.5;
    ctx.save();
    if (season === "spring") {
      // Faint falling cherry blossom petals
      ctx.fillStyle = "rgba(220,160,180,0.12)";
      for (let p = 0; p < 6; p++) {
        const px = screenX + ((p * 2654435761 >>> 0) % 1000) / 1000 * screenW;
        const py = screenY + (((p * 1013904223 + Math.floor(wpTime * 30)) >>> 0) % 1000) / 1000 * screenH;
        const ps = 1.5 + (p % 3);
        ctx.beginPath();
        ctx.ellipse(px, py, ps, ps * 0.6, p * 0.8, 0, Math.PI * 2);
        ctx.fill();
      }
    } else if (season === "summer") {
      // Subtle twinkling stars
      ctx.fillStyle = "rgba(200,220,255,0.2)";
      for (let st = 0; st < 8; st++) {
        const sx = screenX + ((st * 374761393 >>> 0) % 1000) / 1000 * screenW;
        const sy = screenY + ((st * 668265263 >>> 0) % 1000) / 1000 * screenH * 0.6;
        const twinkle = 0.3 + Math.sin(wpTime * 3 + st * 2.1) * 0.7;
        ctx.globalAlpha = 0.15 * Math.max(0, twinkle);
        ctx.beginPath();
        ctx.arc(sx, sy, 0.8, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    } else if (season === "autumn") {
      // Drifting leaf silhouettes
      ctx.fillStyle = "rgba(180,120,40,0.08)";
      for (let lf = 0; lf < 5; lf++) {
        const lx = screenX + ((lf * 1103515245 >>> 0) % 1000) / 1000 * screenW;
        const ly = screenY + (((lf * 1274126177 + Math.floor(wpTime * 20)) >>> 0) % 1000) / 1000 * screenH;
        const lr = Math.sin(wpTime + lf * 1.5) * 0.5;
        ctx.save();
        ctx.translate(lx, ly);
        ctx.rotate(lr);
        ctx.beginPath();
        ctx.ellipse(0, 0, 3, 1.8, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    } else {
      // Frost crystal sparkles
      ctx.strokeStyle = "rgba(180,200,240,0.1)";
      ctx.lineWidth = 0.5;
      for (let fc = 0; fc < 4; fc++) {
        const fx = screenX + ((fc * 2246822519 >>> 0) % 1000) / 1000 * screenW;
        const fy = screenY + ((fc * 1640531527 >>> 0) % 1000) / 1000 * screenH;
        const sparkle = 0.5 + Math.sin(wpTime * 2 + fc * 3.0) * 0.5;
        ctx.globalAlpha = 0.08 * sparkle;
        // Six-pointed star
        for (let arm = 0; arm < 6; arm++) {
          const angle = (arm / 6) * Math.PI * 2;
          ctx.beginPath();
          ctx.moveTo(fx, fy);
          ctx.lineTo(fx + Math.cos(angle) * 3, fy + Math.sin(angle) * 3);
          ctx.stroke();
        }
      }
      ctx.globalAlpha = 1;
    }
    ctx.restore();

    // ── Status Bar ──
    this.drawStatusBar(ctx, screenX, screenY, screenW, state);

    // ── Content Area ──
    const contentX = screenX;
    const contentY = screenY + STATUS_BAR_H;
    const contentW = screenW;
    const contentH = screenH - STATUS_BAR_H - HOME_INDICATOR_H;

    const activeApp = device.activeApp ? getApp(device.activeApp) : null;

    if (activeApp) {
      // App view with navigation bar
      this.drawNavBar(ctx, contentX, contentY, contentW, activeApp.name);

      const appContentRect: Rect = {
        x: contentX,
        y: contentY + NAV_BAR_H,
        w: contentW,
        h: contentH - NAV_BAR_H,
      };

      // Store for handleTap and external systems (input positioning)
      device.appContentRect = appContentRect;

      // Pass pointer position to active app for hover effects
      if (activeApp.handlePointerMove) {
        const localX = this.pointerX - appContentRect.x;
        const localY = this.pointerY - appContentRect.y;
        const inside = localX >= 0 && localX <= appContentRect.w && localY >= 0 && localY <= appContentRect.h;
        activeApp.handlePointerMove(inside ? localX : -1, inside ? localY : -1, state);
      }

      // App slide transition
      if (this.transition.type === "app_enter") {
        const t = easeOutCubic(this.transition.progress);
        ctx.save();
        ctx.translate((1 - t) * contentW, 0);
        ctx.globalAlpha *= t;
        activeApp.drawContent(ctx, appContentRect, state, this.lastDt);
        ctx.restore();
      } else if (this.transition.type === "app_exit") {
        const t = 1 - easeOutCubic(this.transition.progress);
        ctx.save();
        ctx.translate((1 - t) * contentW, 0);
        ctx.globalAlpha *= t;
        activeApp.drawContent(ctx, appContentRect, state, this.lastDt);
        ctx.restore();
      } else {
        activeApp.drawContent(ctx, appContentRect, state, this.lastDt);
      }
    } else {
      // Home screen
      device.appContentRect = null;
      this.drawHomeScreen(ctx, contentX, contentY, contentW, contentH, state, isPad);
    }

    // ── Home Indicator ──
    const indW = isPad ? 60 : 40;
    drawRoundRect(
      ctx,
      clampedCx - indW / 2, screenY + screenH - HOME_INDICATOR_H + 4,
      indW, 4, 2,
      C_INDICATOR
    );

    // ── Inner shadow edges ──
    const innerShadow = ctx.createLinearGradient(screenX, screenY, screenX, screenY + 8);
    innerShadow.addColorStop(0, "rgba(0,0,0,0.25)");
    innerShadow.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = innerShadow;
    ctx.fillRect(screenX, screenY, screenW, 8);

    // ── Glass sheen (subtle reflection across upper-left) ──
    const gloss = ctx.createRadialGradient(
      screenX + screenW * 0.25, screenY + screenH * 0.1, 0,
      screenX + screenW * 0.5, screenY + screenH * 0.5, screenW * 0.8
    );
    gloss.addColorStop(0, "rgba(255,255,255,0.06)");
    gloss.addColorStop(0.4, "rgba(255,255,255,0.02)");
    gloss.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = gloss;
    ctx.fillRect(screenX, screenY, screenW, screenH);

    ctx.restore(); // clip
    ctx.restore(); // transform + alpha

    // Update screen rect for hit-testing (uses clamped position)
    const rect: Rect = { x: frameX, y: frameY, w: devW, h: devH };
    state.ui.deviceScreenRect = rect;
    return rect;
  }

  // ── Status Bar ────────────────────────────────────────────────────────

  private drawStatusBar(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, w: number,
    state: GameState
  ): void {
    // Background
    ctx.fillStyle = "rgba(0,0,0,0.3)";
    ctx.fillRect(x, y, w, STATUS_BAR_H);

    const pad = 8;
    const textY = y + 4;

    // Time (left)
    const hour = Math.floor(state.time.gameHour);
    const min = Math.floor((state.time.gameHour % 1) * 60);
    const timeStr = `${hour.toString().padStart(2, "0")}:${min.toString().padStart(2, "0")}`;
    drawText(ctx, timeStr, x + pad, textY, 50, "bold 9px monospace", C_STATUS_TEXT);

    // Battery (right) — tied to a fun metric
    const batX = x + w - pad - 20;
    const batY = y + 5;
    const batW = 16;
    const batH = 8;
    ctx.strokeStyle = C_STATUS_TEXT;
    ctx.lineWidth = 0.8;
    ctx.strokeRect(batX, batY, batW, batH);
    ctx.fillStyle = C_STATUS_TEXT;
    ctx.fillRect(batX + batW, batY + 2, 2, 4); // nub
    // Fill based on... room warmth? Aeterna energy? Use warmth.
    const mood = state.currentRoom?.mood;
    const fill = mood ? (mood.warmth + mood.coziness) / 2 : 0.5;
    ctx.fillStyle = fill > 0.5 ? "#34c759" : fill > 0.2 ? "#ffcc00" : "#ff3b30";
    ctx.fillRect(batX + 1, batY + 1, (batW - 2) * Math.max(0.05, fill), batH - 2);

    // Signal dots (center-right)
    const sigX = batX - 18;
    for (let i = 0; i < 3; i++) {
      ctx.fillStyle = i < 2 ? C_STATUS_TEXT : "rgba(255,255,255,0.25)";
      ctx.beginPath();
      ctx.arc(sigX + i * 5, batY + 4, 1.5, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // ── Navigation Bar ────────────────────────────────────────────────────

  private drawNavBar(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, w: number,
    title: string
  ): void {
    ctx.fillStyle = C_NAV_BG;
    ctx.fillRect(x, y, w, NAV_BAR_H);

    // Back arrow
    ctx.fillStyle = C_BACK_ARROW;
    ctx.font = "bold 16px sans-serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText("\u2039", x + 8, y + NAV_BAR_H / 2 + 1);

    // Title
    drawText(ctx, title, x + w / 2, y + 10, w - 60, "bold 11px sans-serif", C_NAV_TEXT, "center");

    // Separator line (warm amber accent)
    ctx.strokeStyle = "rgba(232,160,80,0.12)";
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(x, y + NAV_BAR_H);
    ctx.lineTo(x + w, y + NAV_BAR_H);
    ctx.stroke();
  }

  // ── Home Screen ───────────────────────────────────────────────────────

  private drawHomeScreen(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, w: number, h: number,
    state: GameState, isPad: boolean
  ): void {
    const apps = getApps();
    const cols = isPad ? 5 : 4;
    // Dynamically size icons to fit within available width with padding
    const sidePad = 6;
    const availW = w - sidePad * 2;
    const gap = isPad ? ICON_GAP : 8;
    const iconSz = Math.min(ICON_SIZE, Math.floor((availW - gap * (cols - 1)) / cols));
    const cellW = iconSz + gap;
    const cellH = iconSz + ICON_LABEL_SIZE + gap + 4;
    const gridW = cols * iconSz + (cols - 1) * gap;
    const startX = x + (w - gridW) / 2;
    const startY = y + 14;

    apps.forEach((app, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const ix = startX + col * cellW;
      const iy = startY + row * cellH;
      const icx = ix + iconSz / 2;
      const icy = iy + iconSz / 2;
      const iconRadius = Math.max(6, Math.round(iconSz * 0.25));

      // Icon press feedback
      const isPressed = this.iconPressIndex === i && this.iconPressTimer > 0;
      if (isPressed) {
        ctx.save();
        ctx.translate(icx, icy);
        ctx.scale(0.92, 0.92);
        ctx.translate(-icx, -icy);
      }

      // Icon background (gradient rounded rect)
      const iconGrad = ctx.createLinearGradient(ix, iy, ix, iy + iconSz);
      iconGrad.addColorStop(0, app.iconGradient[0]);
      iconGrad.addColorStop(1, app.iconGradient[1]);
      drawRoundRect(ctx, ix, iy, iconSz, iconSz, iconRadius, undefined);
      ctx.fillStyle = iconGrad;
      ctx.fill();

      // Icon content
      app.drawIcon(ctx, icx, icy, iconSz);

      // Badge
      const badge = app.badge?.(state) ?? getAppBadge(state.player.device, app.id);
      if (badge > 0) {
        const bx = ix + iconSz - 4;
        const by = iy - 2;
        const bw = badge > 9 ? 16 : 12;
        ctx.fillStyle = C_BADGE_BG;
        drawRoundRect(ctx, bx - bw / 2, by, bw, 12, 6);
        ctx.fill();
        ctx.font = "bold 8px sans-serif";
        ctx.fillStyle = C_BADGE_TEXT;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(badge > 99 ? "99+" : `${badge}`, bx, by + 6);
      }

      // Label
      drawText(
        ctx, app.name,
        icx, iy + iconSz + 3,
        cellW - 4, `${ICON_LABEL_SIZE}px sans-serif`, C_ICON_LABEL, "center"
      );

      if (isPressed) ctx.restore();
    });
  }

  // ── Bezel hit-test ────────────────────────────────────────────────────

  private isOverBezel(px: number, py: number, fx: number, fy: number, fw: number, fh: number): boolean {
    // Inside frame rect?
    if (px < fx || px > fx + fw || py < fy || py > fy + fh) return false;
    // Inside screen rect? (not bezel)
    const sx = fx + DRAG_BEZEL;
    const sy = fy + DRAG_BEZEL;
    if (px > sx && px < fx + fw - DRAG_BEZEL && py > sy && py < fy + fh - DRAG_BEZEL) return false;
    return true;
  }

  // ── Tap Handling ──────────────────────────────────────────────────────

  /**
   * Handle a tap at screen coordinates (sx, sy).
   * Returns true if the tap was consumed by the overlay.
   */
  handleTap(sx: number, sy: number, state: GameState): boolean {
    const device = state.player.device;
    const rect = state.ui.deviceScreenRect;
    if (!rect || !isDeviceReady(device)) return false;

    // Check if tap is inside the device frame
    if (sx < rect.x || sx > rect.x + rect.w || sy < rect.y || sy > rect.y + rect.h) {
      // Outside device — close it
      this.close(state);
      state.ui.uiSounds.push("device_close");
      return true;
    }

    // Check if tap is on the bezel — start drag
    if (this.isOverBezel(sx, sy, rect.x, rect.y, rect.w, rect.h)) {
      this.dragging = true;
      this.dragStartX = sx;
      this.dragStartY = sy;
      this.dragOffsetStartX = device.offsetX;
      this.dragOffsetStartY = device.offsetY;
      return true;
    }

    // Convert to screen-local coordinates
    const isPad = device.activeDevice === "pad";
    const devW = isPad ? PAD_W : PHONE_W;
    const screenX = rect.x + FRAME_INSET;
    const screenY = rect.y + FRAME_INSET;
    const screenW = devW - FRAME_INSET * 2;
    const localX = sx - screenX;
    const localY = sy - screenY;

    if (localX < 0 || localX > screenW || localY < 0) return true; // in frame, outside screen

    const contentY = STATUS_BAR_H;

    if (device.activeApp) {
      // Check back button — let app handle internal navigation first
      if (localY >= contentY && localY <= contentY + NAV_BAR_H && localX < 30) {
        const app = getApp(device.activeApp);
        if (app?.onBack?.(state)) {
          // App handled it internally (e.g. detail → list view)
          state.ui.uiSounds.push("device_back");
        } else {
          this.navigateBack(state);
          state.ui.uiSounds.push("device_back");
        }
        return true;
      }

      // Start app-drag tracking — the actual tap is dispatched on release
      // if the user doesn't drag far enough
      const app = getApp(device.activeApp);
      if (app) {
        this.appDragging = true;
        this.appDragLastY = sy;
        this.appDragStartY = sy;
        this.appDragStartX = sx;
        this.appDragVelocity = 0;
        this.appDragDist = 0;
        this._pendingTapApp = device.activeApp;
        this._pendingTapX = localX;
        this._pendingTapY = localY - contentY - NAV_BAR_H;
        return true;
      }
    } else {
      // Home screen — find tapped icon
      const apps = getApps();
      const cols = isPad ? 5 : 4;
      const sidePad = 6;
      const availW = screenW - sidePad * 2;
      const hitGap = isPad ? ICON_GAP : 8;
      const hitIconSz = Math.min(ICON_SIZE, Math.floor((availW - hitGap * (cols - 1)) / cols));
      const cellW = hitIconSz + hitGap;
      const cellH = hitIconSz + ICON_LABEL_SIZE + hitGap + 4;
      const gridW = cols * hitIconSz + (cols - 1) * hitGap;
      const startX = (screenW - gridW) / 2;
      const startY = contentY + 14;

      for (let i = 0; i < apps.length; i++) {
        const col = i % cols;
        const row = Math.floor(i / cols);
        const ix = startX + col * cellW;
        const iy = startY + row * cellH;

        if (localX >= ix && localX <= ix + hitIconSz &&
            localY >= iy && localY <= iy + hitIconSz) {
          // Icon tapped — press animation then open
          this.iconPressIndex = i;
          this.iconPressTimer = 0.12;
          this.openApp(apps[i].id, state);
          state.ui.uiSounds.push("app_open");
          return true;
        }
      }
    }

    return true; // consumed (inside device)
  }

  // ── Public API ────────────────────────────────────────────────────────

  /** Open the device (begin raise animation) */
  open(state: GameState, deviceType: "phone" | "pad"): void {
    const device = state.player.device;
    device.activeDevice = deviceType;
    device.activeApp = null;
    device.animPhase = "raising";
    device.animProgress = 0;
    state.ui.deviceOpen = true;
    this.transition = { type: "opening", progress: 0, fromApp: null, toApp: null };
    this.dragging = false;
    // Animation starts from the character's hand position on screen
    this.animOriginX = state.ui.playerScreenX;
    this.animOriginY = state.ui.playerScreenY - 20; // slightly above character center (hand area)

    // Close other panels
    state.ui.inventoryOpen = false;
    state.ui.aeternaOpen = false;
    state.ui.furnitureContextId = null;
  }

  /** Close the device (begin lower animation) */
  close(state: GameState): void {
    const device = state.player.device;
    if (device.activeApp) {
      const app = getApp(device.activeApp);
      app?.onClose?.(state);
    }
    device.animPhase = "lowering";
    this.transition = { type: "closing", progress: 0, fromApp: null, toApp: null };
    this.dragging = false;
    // Animate back toward character's hand
    this.animOriginX = state.ui.playerScreenX;
    this.animOriginY = state.ui.playerScreenY - 20;
    // Reset offset on close so it opens centered next time
    device.offsetX = 0;
    device.offsetY = 0;
  }

  /** Navigate to an app from home screen */
  openApp(appId: string, state: GameState): void {
    const app = getApp(appId);
    if (!app) return;
    app.onOpen?.(state);
    this.transition = { type: "app_enter", progress: 0, fromApp: null, toApp: appId };
  }

  /** Navigate back from app to home screen */
  navigateBack(state: GameState): void {
    const device = state.player.device;
    if (device.activeApp) {
      const app = getApp(device.activeApp);
      app?.onClose?.(state);
      this.transition = { type: "app_exit", progress: 0, fromApp: device.activeApp, toApp: null };
    }
  }

  /**
   * Forward scroll-wheel input to the active app.
   * wheelDelta is the same value from InputState.wheelZoomDelta
   * (negative = scroll up = content moves down).
   */
  handleWheel(wheelDelta: number, state: GameState): void {
    const device = state.player.device;
    if (!device.activeApp) return;
    const app = getApp(device.activeApp);
    if (!app?.handleDrag) return;
    // Convert zoom-style delta to pixel scroll (scale feels natural)
    const scrollPx = -wheelDelta * 300;
    app.handleDrag(scrollPx, state);
  }

  /** Toggle device open/close */
  toggle(state: GameState, deviceType: "phone" | "pad" = "phone"): void {
    if (state.ui.deviceOpen) {
      this.close(state);
    } else {
      this.open(state, deviceType);
    }
  }
}

// ─── Easing Functions ─────────────────────────────────────────────────────

function easeOutBack(t: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

// ─── Color Utility ────────────────────────────────────────────────────────

function darken(hex: string, amount: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const f = 1 - amount;
  return `#${Math.round(r * f).toString(16).padStart(2, "0")}${Math.round(g * f).toString(16).padStart(2, "0")}${Math.round(b * f).toString(16).padStart(2, "0")}`;
}
